/**
 * Contrato de cable del canal de avisos (`retail-notifications-websocket`). El sobre lo arma el
 * lambda y NO trae datos del comprador: el detalle se lo pide el front al wizard.
 */

/** Tipos de aviso que este front sabe pintar. Espeja `NotificationTypeEnum` del lambda. */
export const SALE_UPDATED = 'SALE_UPDATED';

export type SaleNotificationType = typeof SALE_UPDATED;

export interface SaleNotification {
    type: SaleNotificationType;
    /** Venta a la que se refiere el aviso. */
    sessionId: string;
    /** ISO-8601 con la hora en que el lambda lo emitió. */
    emittedAt: string;
}

/**
 * Resultado del parseo. El caso ilegible lleva el `type` crudo —y NADA más— para poder reportar el
 * drift del contrato sin sacar el `sessionId` del navegador.
 */
export type ParsedEnvelope =
    | { ok: true; notification: SaleNotification }
    | { ok: false; type: string | null };

const KNOWN_TYPES = new Set<string>([SALE_UPDATED]);

/** El único frame que el canal acepta del navegador: cualquier otro tipo se rechaza con 400. */
export const HEARTBEAT_FRAME = JSON.stringify({ type: 'heartbeat' });

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0;
}

/**
 * Sobre crudo del socket → aviso tipado. Se descarta lo que no se puede afirmar: un tipo desconocido
 * pintado como venta actualizada le mentiría al vendedor sobre una venta real.
 */
export function parseNotification(raw: unknown): ParsedEnvelope {
    if (typeof raw !== 'string') return { ok: false, type: null };

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return { ok: false, type: null };
    }

    if (typeof parsed !== 'object' || parsed === null) return { ok: false, type: null };
    const wire = parsed as Record<string, unknown>;
    const type = isNonEmptyString(wire.type) ? wire.type : null;

    if (type === null || !KNOWN_TYPES.has(type)) return { ok: false, type };
    if (!isNonEmptyString(wire.sessionId)) return { ok: false, type };
    if (!isNonEmptyString(wire.emittedAt)) return { ok: false, type };

    return {
        ok: true,
        notification: {
            type: type as SaleNotificationType,
            sessionId: wire.sessionId,
            emittedAt: wire.emittedAt,
        },
    };
}
